<script>
  export let total = 0;
  export let current = 0;
  export let onDotClick = (index) => {};
  export let className = "";

  // Track previous position for animations
  let previousCurrent = current;
  $: if (previousCurrent !== current) {
    previousCurrent = current;
  }
</script>

<div
  class="flex justify-center items-center gap-3 md:gap-6 mt-6 md:mt-8 {className}"
>
  {#each Array(total) as _, i}
    <button
      class="relative p-2 md:p-3 group focus:outline-none focus-visible:ring-2
        focus-visible:ring-blue-500/50 dark:focus-visible:ring-blue-400/50 rounded-full
        transition-transform duration-300 hover:scale-110 active:scale-95"
      on:click={() => onDotClick(i)}
      aria-label="Go to slide {i + 1}"
    >
      <!-- Main dot container with 3D effect -->
      <div
        class="relative transform transition-transform duration-300
        group-hover:rotate-[360deg]"
      >
        <!-- Base dot with glass effect -->
        <div
          class="w-2 md:w-2.5 h-2 md:h-2.5 rounded-full transition-all duration-500 ease-out-expo
          backdrop-blur-sm border border-white/20 dark:border-white/10
          {current === i
            ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 scale-125'
            : 'bg-gradient-to-r from-gray-300/80 to-gray-200/80 dark:from-gray-600/80 dark:to-gray-700/80 scale-100'}
          group-hover:shadow-lg group-hover:shadow-blue-500/20 dark:group-hover:shadow-blue-400/20
          relative z-10"
        >
          <!-- Glass shine effect -->
          <div
            class="absolute inset-0 rounded-full bg-gradient-to-br from-white/50 via-transparent to-black/20
            dark:from-white/30 dark:to-black/30"
          ></div>
        </div>

        <!-- Active dot effects -->
        {#if current === i}
          <!-- Neon glow -->
          <div
            class="absolute inset-[-6px] md:inset-[-8px] rounded-full opacity-75
            bg-gradient-to-r from-blue-500/30 via-purple-500/30 to-pink-500/30
            blur-[6px] -z-10 animate-glow"
          ></div>

          <!-- Orbital rings -->
          <div
            class="absolute inset-[-8px] md:inset-[-12px] rounded-full border border-blue-500/20
            animate-spin-slow"
          ></div>
          <div
            class="absolute inset-[-7px] md:inset-[-10px] rounded-full border border-purple-500/20
            animate-spin-slow-reverse"
          ></div>

          <!-- Ripple effect on change -->
          <div
            class="absolute inset-[-15px] md:inset-[-20px] rounded-full
            bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10
            animate-ripple-fade"
          ></div>
        {/if}

        <!-- Interactive hover effects -->
        <div
          class="absolute inset-[-6px] md:inset-[-8px] rounded-full
          bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500
          opacity-0 group-hover:opacity-20 transition-all duration-500
          scale-0 group-hover:scale-100 blur-sm"
        ></div>
      </div>

      <!-- Progress line with flow effect - hide on small screens -->
      {#if i < total - 1}
        <div class="absolute top-1/2 left-[100%] w-2 md:w-6 -translate-y-1/2">
          <div
            class="relative h-0.5 w-full transition-all duration-700 ease-out-expo overflow-hidden rounded-full
            {i < current
              ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 scale-x-100'
              : 'bg-gray-200/50 dark:bg-gray-700/50 scale-x-75'}"
          >
            <!-- Line effects -->
            {#if i < current}
              <!-- Flowing light effect -->
              <div
                class="absolute inset-0 w-[200%] bg-gradient-to-r from-transparent
                via-white/50 to-transparent -translate-x-full animate-flow"
              ></div>
            {/if}
          </div>
        </div>
      {/if}
    </button>
  {/each}
</div>

<style>
  @keyframes glow {
    0%,
    100% {
      opacity: 0.5;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.1);
    }
  }

  @keyframes flow {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  @keyframes ripple-fade {
    0% {
      transform: scale(0.8);
      opacity: 0.5;
    }
    100% {
      transform: scale(1.5);
      opacity: 0;
    }
  }

  .animate-glow {
    animation: glow 3s ease-in-out infinite;
  }

  .animate-flow {
    animation: flow 2s linear infinite;
  }

  .animate-spin-slow {
    animation: spin 8s linear infinite;
  }

  .animate-spin-slow-reverse {
    animation: spin 6s linear infinite reverse;
  }

  .animate-ripple-fade {
    animation: ripple-fade 1s ease-out forwards;
  }

  .ease-out-expo {
    transition-timing-function: cubic-bezier(0.19, 1, 0.22, 1);
  }

  /* Mobile-specific styles */
  @media (max-width: 768px) {
    /* Smaller touch targets but still accessible */
    button {
      touch-action: manipulation;
    }
  }
</style>
